import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AuthStatus, useAuth } from '../auth';
import { ToastContainer, toast } from 'react-toastify';
import { useLocation, useNavigate } from "react-router-dom";
import { set, useForm } from "react-hook-form";

import NavBar from './navbar';
import Menu from './menu';

import '../styles/contract.scss';
import { Popover } from 'bootstrap';

const endpoint = process.env.REACT_APP_ENDPOINT;
const parseStoredJson = (value, fallback) => {
    if (typeof value !== 'string') return value ?? fallback;
    if (!value) return fallback;
    return JSON.parse(value) ?? fallback;
};

const Contract = () => {
    const status = AuthStatus();
    const { token } = useAuth();
    const location = useLocation();
    const { register, handleSubmit, watch, setValue, formState:{errors}, reset } = useForm();

    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [collaborators, setCollaborators] = useState([]);
    const [filteredCollaborators, setFilteredCollaborators] = useState([]);
    const [selectedCollaboratorId, setSelectedCollaboratorId] = useState("");
    const [selectedCollaborator, setSelectedCollaborator] = useState({});
    const [companyAddresses, setCompanyAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [currentContractId, setCurrentContractId] = useState(null);
    const [superior, setSuperior] = useState(false);

    // Obtener tipo de contrato y datos existentes si estamos editando
    const contractType = location.state?.new;
    const existingContract = location.state?.contract;

    const navigate = useNavigate();

    const periodo = watch("periodo");

    const daysOfWeek = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
    const daysOfMonth = ["Primer día del mes", ...Array.from({ length: 30 }, (_, i) => i + 1), "Último día del mes"];
    
    const getPaymentDays = () => {
        if (periodo === "Semanal") return 1;
        if (periodo === "Decenal") return 3;
        return 2;
    };
    
    // Obtener empresas
    const fetchCompanies = async () => {
        try {
            const response = await axios.get(`${endpoint}admin/companies`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCompanies(response.data);
            console.log(response.data);
            if (response.data.length > 0 && !existingContract) {
                setSelectedCompany(response.data[0]); // Selecciona la primera empresa solo si no hay contrato existente
            }
        } catch (error) {
            toast.error('Error al obtener la lista de compañías');
        }
    };

    // Obtener colaboradores
    const fetchCollaborators = async () => {
        try {
            const response = await axios.get(`${endpoint}admin/collaborators`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCollaborators(response.data);
        } catch (error) {
            toast.error('Error al obtener la lista de colaboradores');
        }
    };

    // Filtrar colaboradores cuando cambia la empresa seleccionada
    useEffect(() => {
        if (selectedCompany) {
            const filtered = collaborators.filter(col => col.company_id === selectedCompany.id);
            setFilteredCollaborators(filtered);
            if (!existingContract) {
                setSelectedCollaboratorId(""); // Reiniciar selección solo si no hay contrato existente
            }
            console.log(selectedCompany)
            setCompanyAddresses(selectedCompany.addresses || []);
            
            if (!existingContract) {
                setSelectedAddress(""); // Reiniciar selección de dirección solo si no hay contrato existente
            }
            if(selectedCompany.benefits){
                setValue('aguinaldo',selectedCompany.benefits.aguinaldo)
                setValue('prima_vacacional',selectedCompany.benefits.prima_vacacional)
            }
        }
    }, [selectedCompany, collaborators]);

    useEffect(() => {
        if (selectedCollaboratorId) {
            const collaborator = collaborators.find(c => c.id == selectedCollaboratorId);
            console.log(collaborators)
            console.log(collaborator)
          
            // Si el colaborador existe, establecer el puesto en el formulario
            if (collaborator) {
                setValue('puesto', collaborator.puesto);
                setValue('nombre', collaborator.nombre + ' ' + collaborator.apellidos)
            }
        }
    }, [selectedCollaboratorId, collaborators, setValue]);

    useEffect(()=>{
        let address = companyAddresses.find(a => a.id == selectedAddress)
        if(address){
            setValue('calle', address.calle)
            setValue('numero_exterior',address.numero_exterior)
            setValue('numero_interior',address.numero_interior)
            setValue('ciudad',address.ciudad)
            setValue('estado', address.estado)
            setValue('colonia', address.colonia)
            setValue('codigo_postal', address.codigo_postal)
        }
    },[selectedAddress])

    // Cargar datos del contrato existente
    useEffect(() => {
        if (existingContract) {
            loadContractData(existingContract);
        }
    }, [existingContract, companies, collaborators]);

    // Función para cargar datos del contrato existente en el formulario
    const loadContractData = async (contract) => {
        try {
            setIsEditing(true);
            setCurrentContractId(contract.id);
            
            // Establecer empresa seleccionada
            const company = companies.find(c => c.id === contract.company_id);
            if (company) {
                setSelectedCompany(company);
            }

            // Establecer colaborador seleccionado
            setSelectedCollaboratorId(contract.collaborator_id.toString());

            // Establecer dirección seleccionada
            setSelectedAddress(contract.address_id.toString());

            // Cargar datos básicos del contrato
            setValue('nombre', contract.nombre);
            setValue('puesto', contract.puesto);
            setValue('actividad', contract.actividad);
            setValue('codigo_postal', contract.codigo_postal);
            setValue('periodo', contract.periodo_pago);
            setValue('salario', contract.salario);
            setValue('prima_vacacional', contract.prima_vacacional);
            setValue('aguinaldo', contract.aguinaldo_dias);
            setValue('calle', contract.calle);
            setValue('numero_exterior', contract.numero_exterior);
            setValue('numero_interior', contract.numero_interior || '');
            setValue('colonia', contract.colonia);
            setValue('ciudad', contract.ciudad);
            setValue('estado', contract.estado);
            setValue('fecha_celebracion', contract.fecha_celebracion.split('T')[0]);
            setValue('lugar_ciudad', contract.lugar_ciudad);
            setValue('lugar_estado', contract.lugar_estado);

            // Cargar días de pago
            const diasPago = parseStoredJson(contract.dias_pago, []);
            diasPago.forEach((dia, index) => {
                setValue(`pagos.${index}`, dia);
            });

            // Cargar vacaciones
            const vacacionesData = parseStoredJson(contract.vacaciones, {});
            setValue('vacaciones', vacacionesData);

            // Cargar jornada laboral
            const jornadaData = parseStoredJson(contract.jornada_laboral, {});
            Object.keys(jornadaData).forEach(key => {
                if (key !== 'horarioVariable' && key !== 'horarioFijo') {
                    setValue(key, jornadaData[key]);
                }
            });

            // Cargar horario fijo si existe
            if (jornadaData.horarioFijo) {
                setValue('horarioFijo.desdeDia', jornadaData.horarioFijo.desdeDia);
                setValue('horarioFijo.hastaDia', jornadaData.horarioFijo.hastaDia);
                if (jornadaData.horarioFijo.horas) {
                    Object.keys(jornadaData.horarioFijo.horas).forEach(horaKey => {
                        setValue(`horarioFijo.horas.${horaKey}`, jornadaData.horarioFijo.horas[horaKey]);
                    });
                }
            }

            // Cargar datos específicos del tipo de contrato
            if (contract.additionalFields) {
                console.log(contract.additionalFields)
                Object.keys(contract.additionalFields).forEach(key => {
                    if (key !== 'id' && key !== 'created_at' && key !== 'updated_at') {
                        setValue(key, contract.additionalFields[key]);
                    }
                });
            }

            console.log('Contrato cargado para edición:', contract);

        } catch (error) {
            console.error('Error al cargar datos del contrato:', error);
            toast.error('Error al cargar el contrato para edición');
        }
    };

    useEffect(() => {
        fetchCompanies();
        fetchCollaborators();

        var popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'));
        var popoverList = popoverTriggerList.map(function (popoverTriggerEl) {
            return new Popover(popoverTriggerEl, {
                trigger: 'focus' // Se cierra cuando el usuario hace clic fuera
            });
        });
    }, [token]);

    const onSubmit = async (data) => {
        try {
            // Validaciones adicionales
            if (!selectedCollaboratorId) {
                toast.error('Selecciona un colaborador');
                return;
            }

            if (!selectedCompany) {
                toast.error('Selecciona una empresa');
                return;
            }

            if (!selectedAddress) {
                toast.error('Selecciona una dirección');
                return;
            }

            // Validar campos requeridos específicos
            if (!data.actividad || data.actividad.trim() === '') {
                toast.error('La actividad a realizar es requerida');
                return;
            }

            if (!data.pagos || data.pagos.length === 0 || data.pagos.every(p => !p)) {
                toast.error('Selecciona al menos un día de pago');
                return;
            }

            // Preparar datos de jornada laboral
            const jornadaLaboral = {
                turno: data.turno,
                tipoHorario: data.tipoHorario,
                tipoJornada: data.tipoJornada,
                diasDescanso: data.diasDescanso,
                tiempoDescanso: data.tiempoDescanso || null,
                horarioFijo: data.tipoHorario.includes('fijo') ? {
                    desdeDia: data.horarioFijo?.desdeDia,
                    hastaDia: data.horarioFijo?.hastaDia,
                    horas: {
                        inicio: data.horarioFijo?.horas?.inicio,
                        fin: data.horarioFijo?.horas?.fin,
                        inicio2: data.horarioFijo?.horas?.inicio2 || null,
                        fin2: data.horarioFijo?.horas?.fin2 || null
                    }
                } : null,
                horarioVariable: data.tipoHorario.includes('variable') ? 
                    (data.horarioVariableRows || []).filter(row => row.dia).map(row => ({
                        dia: row.dia,
                        horas: {
                            inicio: row.horas?.inicio,
                            fin: row.horas?.fin,
                            inicio2: row.horas?.inicio2 || null,
                            fin2: row.horas?.fin2 || null
                        }
                    })) : null
            };

            // Preparar datos para enviar
            const contractData = {
                nombre: data.nombre,
                puesto: data.puesto,
                actividad: data.actividad.trim(),
                calle: data.calle,
                numero_exterior: data.numero_exterior,
                numero_interior: data.numero_interior || null,
                colonia: data.colonia,
                ciudad: data.ciudad,
                estado: data.estado,
                codigo_postal: data.codigo_postal,
                periodo: data.periodo_pagos !== '' ? data.periodo_pagos : data.periodo,
                salario: parseFloat(data.salario),
                prima_vacacional: parseFloat(data.prima_vacacional),
                aguinaldo: parseInt(data.aguinaldo),
                vacaciones: data.vacaciones && Object.keys(data.vacaciones).length > 0 
                ? data.vacaciones 
                : {
                    first_year: 6,
                    ranges: [
                        {
                            step: 1,
                            days: 2,
                            limit: 5
                        }
                    ]
                },
                jornada_laboral: jornadaLaboral,
                fecha_celebracion: data.fecha_celebracion,
                lugar_ciudad: data.lugar_ciudad,
                lugar_estado: data.lugar_estado,
                collaborator_id: selectedCollaboratorId,
                company_id: selectedCompany.id,
                address_id: selectedAddress,
                tipo: contractType,
                pagos: data.periodo_pagos !== '' ? ['Otro'] : data.pagos.filter(p => p),
                ...processContractTypeData(data, contractType)
            };

            console.log('Datos a enviar:', contractData)

            let response;
            if (isEditing && currentContractId) {
                // Actualizar contrato existente
                response = await axios.put(`${endpoint}admin/contracts/${currentContractId}`, contractData, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } else {
                // Crear nuevo contrato
                response = await axios.post(`${endpoint}admin/contracts`, contractData, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }

            if (response.data.success) {
                toast.success(isEditing ? 'Contrato actualizado exitosamente' : 'Contrato creado exitosamente');
                console.log(response);
                setTimeout(() => navigate("/admin/documents", { state: {selectedCompany: selectedCompany} }), 3500)
            } else {
                throw new Error(response.data.message || 'Error en la respuesta del servidor');
            }
            
        } catch (error) {
            console.error('Error al crear/actualizar contrato:', error);
            
            const errorMessage = error.response?.data?.message || 
                                error.response?.data?.error || 
                                error.message || 
                                'Error al crear/actualizar el contrato';
            
            toast.error(errorMessage);
            
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach(err => {
                    toast.error(err[0]);
                });
            }
        }
    };

    const processContractTypeData = (data, type) => {
      const specificData = {};
      
      if (!type) {
        console.error('Tipo de contrato no especificado');
        return specificData;
      }

      switch(type) {
        case 'determinado':
          specificData.motivo = data.motivo;
          specificData.duracion = data.duracion || '';
          break;
          
        case 'obra':
          specificData.cliente = data.cliente || '';
          specificData.descripcion_obra = data.descripcion_obra || '';
          break;
          
        case 'indeterminado_prueba':
          specificData.prueba = data.prueba;
          specificData.tipo_empleado = data.tipo_empleado || '';
          break;
          
        case 'indeterminado_capacitacion':
          specificData.prueba = data.prueba;
          specificData.tipo_empleado = data.tipo_empleado || '';
          break;
          
        case 'indeterminado_temporada':
          specificData.razon_temporada = data.razon || '';
          specificData.duracion_temporada = data.temporada || '';
          specificData.fin_temporada = data.fin_temporada || '';
          break;
        case 'indeterminado':
          break;

        default:
          console.warn(`Tipo de contrato no reconocido: ${type}`);
          break;
      }
      
      return specificData;
    };

    // Función para resetear el formulario
    const handleDiscard = () => {
        reset();
        setSelectedCollaboratorId("");
        setSelectedAddress("");
        setIsEditing(false);
        setCurrentContractId(null);
        toast.info('Formulario reiniciado');
    };

    const handlePrestacionesChange = (e) =>{
      const minimas = {
        first_year: 12, // Días mínimos para el primer año
        ranges: [
          { step: 1, days: 2, limit: 5 },   
          { step: 5, days: 2, }, 
          
        ]
      }

      if(e.target.value === ''){
        setSuperior(false);
        setValue('prima_vacacional',25);
        setValue('aguinaldo',15);
        setValue('vacaciones',minimas);

      }else{
        setSuperior(true);
      }
    }
        
    return (
        <div className="contract">
            <NavBar auth={status} companies={companies} selected={selectedCompany} set={setSelectedCompany} />
            <div className='contract-row'>
                <Menu />
                <form className='contract-wrapper' onSubmit={handleSubmit(onSubmit)}>
                    <ToastContainer />
                    <h2>{isEditing ? 'Editar documento' : 'Nuevo documento'}</h2>
                    <div className='contract-controls'>
                        <button type='button' className='btn btn-secondary' onClick={handleDiscard}>
                            Descartar
                        </button>
                        <button type='submit' className='btn btn-primary'>
                            {isEditing ? 'Actualizar contrato' : 'Guardar cambios'}
                        </button>
                    </div>

                    <div className="contract-collaborator">
                        <label htmlFor="collaborator-select">Colaborador</label>
                        <select
                            required
                            id="collaborator-select"
                            value={selectedCollaboratorId}
                            onChange={(e) => setSelectedCollaboratorId(e.target.value)}
                        >
                            <option value="">Selecciona una opción</option>
                            {filteredCollaborators.map(collaborator => (
                                <option key={collaborator.id} value={collaborator.id}>
                                    {collaborator.nombre + ' ' + collaborator.apellidos} 
                                </option>
                            ))}
                        </select>
                    </div>

                    <strong>Condiciones de la relación laboral</strong>
                    <div className="contract-info">

                        <div className="c-input">
                            <label htmlFor="puesto">Puesto</label>
                            <input type="text" name='puesto' required
                                {...register("puesto", { required: "Especifica el puesto", value: selectedCollaborator.puesto })}/>
                        </div>
                        <div className="c-input">
                            <label htmlFor="actividad">
                                Actividad a realizar
                                <button type="button" class="btn btn-help" data-bs-container="body" data-bs-toggle="popover" data-bs-placement="right"  data-bs-trigger="focus"
                                    data-bs-content="Describe con la mayor precisión posible las actividades que el colaborador va a desempeñar"
                                >
                                 ?
                                </button>
                            </label>
                            <textarea name="actividad" id="" required
                            {...register("actividad", { required: "Especifica actividad" })} 
                            ></textarea>
                        </div>

                        <div className="c-input">
                            <label htmlFor="address-select">Sucursal / Centro de trabajo</label>
                            <select
                                id="address-select"
                                value={selectedAddress}
                                onChange={(e) => setSelectedAddress(e.target.value)}
                                required
                            >
                                <option value="">Selecciona una dirección</option>
                                {companyAddresses.map((address) => (
                                    <option key={address.id} value={address.id}>
                                        {address.calle} {address.numero_exterior}, {address.colonia}, {address.ciudad}, {address.estado}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="c-input">
                            <label htmlFor="">Calle</label>
                            <input type="text" 
                                {...register("calle", { required: "Especifica calle", value: selectedAddress.calle })} 
                            />
                        </div>

                        <div className='c-50'>
                            <div className="c-input">
                                <label htmlFor="">Número exterior</label>
                                <input type="text" 
                                    {...register("numero_exterior", { required: "Especifica", value: selectedAddress.estado })} 
                                />
                            </div>
                            <div className="c-input">
                                <label htmlFor="">Número interior</label>
                                <input type="text" 
                                    {...register("numero_interior", { })} 
                                />
                            </div>
                        </div>

                        <div className='c-33'>
                            <div className="c-input">
                                <label htmlFor="">Codigo postal</label>
                                <input type="text" 
                                    {...register("codigo_postal", { required: "Especifica cp", value: selectedAddress.codigo_postal })} 
                                />
                            </div>
                            <div className="c-input">
                                <label htmlFor="">Colonia</label>
                                <input type="text" 
                                    {...register("colonia", { required: 'Especifica colonia' })} 
                                />
                            </div>
                        </div>
                        
                        <div className="c-input">
                            <label htmlFor="">Estado</label>
                            <input type="text" 
                                {...register("estado", { required: "Especifica estado", value: selectedAddress.estado })} 
                            />
                        </div>

                        <div className="c-input">
                            <label htmlFor="">Ciudad</label>
                            <input type="text" 
                                {...register("ciudad", { required: "Especifica calle", value: selectedAddress.ciudad })} 
                            />
                        </div>
                    </div>

                    <strong>Del salario</strong>
                    <div className="contract-pay">
                        <div className="c-input">
                            <label htmlFor="periodo">Periodo de pago</label>
                            <input 
                                type="text"  
                                list='periodo'
                                {...register("periodo", { required: "Especifica el periodo" })}
                            />
                            <datalist id="periodo">
                            <option value="Semanal" />
                            <option value="Decenal" />
                            <option value="Quincenal" />
                        </datalist>
                        </div>

                          <div className="c-input">
                            <label htmlFor="">Si lo prefiere, describe el Periodo de Pago Textualmente</label>
                            <textarea 
                              maxLength={255}
                              {...register("periodo_pagos")} 
                            />
                        </div>

                        <div className="c-input" >
                            <label htmlFor="periodo">Salario (MXN)</label>
                            <span>
                                <p>$</p>
                                <input
                                    className='salario' 
                                    type="number"  
                                    name='salario'
                                    {...register("salario", { required: "Especifica el salario en moneda nacional" })}
                                />
                            </span>
                        </div>

                        {periodo === "Semanal" ? (
                            <div className='c-input'>
                            <label>Día de pago</label>
                            <select {...register("pagos.0")}> 
                                {daysOfWeek.map((day, index) => (
                                <option key={index} value={day}>{day}</option>
                                ))}
                            </select>
                            </div>
                        ) : periodo ? (
                            <div className='c-input'>
                            {[...Array(getPaymentDays())].map((_, i) => (
                                <div key={i} className='c-input'>
                                <label>Día del {i === 0 ? "primer" : i === 1 ? "segundo" : "tercer"} pago</label>
                                <select {...register(`pagos.${i}`)}>
                                    {daysOfMonth.map((day, index) => (
                                    <option key={index} value={day}>{day}</option>
                                    ))}
                                </select>
                                </div>
                            ))}
                            </div>
                        ) : null}
                    </div>

                    <strong>Prestaciones</strong>
                    <div className="contract-vacations">
                        <div className="c-input">
                          <label htmlFor="prestaciones">Prestaciones</label>
                          <select name="prestaciones" id="prestaciones" onChange={(e)=>{handlePrestacionesChange(e)}}>
                            <option value="">Minimas de Ley</option>
                            <option value="superiores">Superiores</option>
                          </select>
                        </div>
                        <VacationYearsInput 
                            disabled={!superior} 
                            data={{}} 
                            value={watch("vacaciones")}
                            onChange={(data) => setValue("vacaciones", data)}
                        />

                        <div className="c-input">
                            <label htmlFor="">Prima vacacional %</label>
                            <input type="text"  disabled={!superior}
                                {...register("prima_vacacional", { required: "Especifica la prima vacacional"})} 
                            />
                        </div>
                        <div className="c-input">
                            <label htmlFor="">Aguinaldo (dias)</label>
                            <input type="text" disabled={!superior}
                                {...register("aguinaldo", { required: "Especifica dias de aguinaldo"})} 
                            />
                        </div>

                        <label htmlFor="">¿Tiene otras prestaciones? 
                          <button type="button" class="btn btn-help" data-bs-container="body" data-bs-toggle="popover" data-bs-placement="right"  data-bs-trigger="focus"
                            data-bs-content="Si el colaborador tiene otras prestaciones como bonos, vales de despensa, fondo de ahorro, etc. especifícalo aquí."
                          >
                            ?
                          </button>
                        </label>
                        <DynamicInputList />

                    </div>

                    <strong>Jornada Laboral</strong>
                    <div className="contract-time">
                      <JornadaLaboralForm 
                        register={register} 
                        watch={watch} 
                        setValue={setValue} 
                        errors={errors} 
                        initialData={existingContract ? parseStoredJson(existingContract.jornada_laboral, {}) : null}
                      />                        
                    </div>

                    <strong>Datos del contrato</strong>
                    <div className="contract-data">
                        <AdditionalFields 
                            contractType={contractType} 
                            register={register} 
                            setValue={setValue}
                            existingData={existingContract?.additionalFields}
                        />

                        <div className="c-input">
                            <label htmlFor="">Fecha de firma/celebración</label>
                            <input type="date" 
                                {...register("fecha_celebracion", { required: "Especifica dias de aguinaldo"})} 
                            />
                        </div>
                        <div className="c-input">
                            <label htmlFor="">Ciudad</label>
                            <input type="text" 
                                {...register("lugar_ciudad", { required: "Especifica ciudad"})} 
                            />
                        </div>
                        <div className="c-input">
                            <label htmlFor="">Estado</label>
                            <input type="text" 
                                {...register("lugar_estado", { required: "Especifica estado"})} 
                            />
                        </div>
                    </div>

                    <button id='submit' className='btn btn-primary'>
                        {isEditing ? 'Actualizar documento' : 'Crear documento'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const VacationYearsInput = ({ data = {}, edit = false, disabled = false, value, onChange }) => {
    const [vacationData, setVacationData] = useState({
      first_year: 12, // Días mínimos para el primer año
      ranges: [
        { step: 1, days: 2, limit: 5 },   
        { step: 5, days: 2, }, 
        
      ]
    });

    // Efecto para sincronizar con el valor del formulario
    useEffect(() => {
        if (value && typeof value === 'object') {
            setVacationData(value);
        }
    }, [value]);

    const handleInputChange = (field, value) => {
        const newData = {
            ...vacationData,
            [field]: parseInt(value) || ''
        };
        setVacationData(newData);
        if (onChange) {
            onChange(newData);
        }
    };

    const handleRangeChange = (index, field, value) => {
        const updatedRanges = [...vacationData.ranges];
        updatedRanges[index] = {
            ...updatedRanges[index],
            [field]: parseInt(value) || ''
        };

        const filteredRanges = updatedRanges.filter(
            (entry, idx) => idx === updatedRanges.length - 1 || entry.step || entry.days || entry.limit
        );

        const newData = { ...vacationData, ranges: filteredRanges };
        setVacationData(newData);

        const lastEntry = filteredRanges[filteredRanges.length - 1];
        if (lastEntry.step && lastEntry.days && lastEntry.limit) {
            const finalData = {
                ...newData,
                ranges: [...filteredRanges, { step: '', days: '', limit: '' }]
            };
            setVacationData(finalData);
            if (onChange) {
                onChange(finalData);
            }
        } else {
            if (onChange) {
                onChange(newData);
            }
        }
    };

    // useEffect(() => {
    //     console.log(data)
    //     setVacationData(Array.isArray(data.ranges) ? data : { first_year: 12, ranges: [{ step: '', days: '', limit: '' }] });
    // }, [data]);

    return (
        <div className="vacation-input-component">
            <div className="c-input">
                 <div className="vacation-input-header">
                    <label htmlFor="">Días de vacaciones al cumplir un año</label>
                </div>
                <input
                    type="number"
                    min="1"
                    value={vacationData.first_year || 12}
                    onChange={(e) => handleInputChange('first_year', e.target.value)}
                    className="form-control"
                    disabled={disabled}
                    placeholder="Días"
                />
            </div>
           
            <div className="vacation-input-header mt-3">
                <label htmlFor="">Incremento de días de vacaciones luego del primer año:</label>
            </div>

            <div className="vacation-input-item">
                <label htmlFor="">Incrementa cada</label>
                <label htmlFor="">Aumento de días</label>
                <label htmlFor="">Hasta los (años)</label>
            </div>

            {vacationData.ranges && vacationData.ranges.map((entry, index) => (
                <div key={index} className="vacation-input-item">
                    <select
                        value={entry.step || ''}
                        onChange={(e) => handleRangeChange(index, 'step', e.target.value)}
                        className="form-control"
                        disabled={disabled}
                    >
                        <option value="">Años</option>
                        {[...Array(10).keys()].map(num => {
                            const value = num + 1;
                            return (
                                <option key={value} value={value}>
                                    {value === 1 ? "Año" : `${value} años`}
                                </option>
                            );
                        })}
                    </select>
                                          
                    <input
                        type="number"
                        min="1"
                        value={entry.days || ''}
                        onChange={(e) => handleRangeChange(index, 'days', e.target.value)}
                        className="form-control"
                        disabled={disabled}
                        placeholder='Días'
                    />
                    <input
                        type="number"
                        min="1"
                        value={entry.limit || ''}
                        onChange={(e) => handleRangeChange(index, 'limit', e.target.value)}
                        className="form-control"
                        disabled={disabled}
                        placeholder="año(s)"
                    />
                </div>
            ))}

            <input
                type="hidden"
                name={edit ? "edit-vacaciones_anuales" : "vacaciones_anuales"}
                value={JSON.stringify(vacationData)}
            />
        </div>
    );
};

const DynamicInputList = ({ data = [], edit = false, disabled = false }) => {
    const [items, setItems] = useState(
        Array.isArray(data) && data.length > 0
            ? data
            : [{ concepto: "", base: "", unidad: "" }]
    );

    const handleInputChange = (index, field, value) => {
        const updatedItems = [...items];
        updatedItems[index] = { ...updatedItems[index], [field]: value };

        // Filtrar líneas vacías que no sean la última
        const filteredItems = updatedItems.filter(
            (entry, idx) => idx === updatedItems.length - 1 || entry.concepto || entry.base || entry.unidad
        );

        setItems(filteredItems);
        // Si la última línea está completa, agregar una nueva vacía
        const lastEntry = filteredItems[filteredItems.length - 1];
        if (lastEntry.concepto && lastEntry.base && lastEntry.unidad) {
            setItems([...filteredItems, { concepto: "", base: "", unidad: "" }]);
        }
    };

    // useEffect(() => {
    //     setItems(Array.isArray(data) && data.length > 0 ? data : [{ concepto: "", base: "", unidad: "" }]);
    // }, [data]);

    return (
        <div className="dynamic-input-list">
            <div className="dynamic-input-header">
                <label>Concepto</label>
                <label>Base</label>
                <label>Unidad</label>
            </div>

            {items.map((entry, index) => (
                <div key={index} className="dynamic-input-item">
                    <input
                        type="text"
                        value={entry.concepto}
                        onChange={(e) => handleInputChange(index, "concepto", e.target.value)}
                        className="form-control"
                        disabled={disabled}
                        placeholder="Concepto"
                    />
                    <input
                        type="text"
                        value={entry.base}
                        onChange={(e) => handleInputChange(index, "base", e.target.value)}
                        className="form-control"
                        disabled={disabled}
                        placeholder="Base"
                    />
                    <select
                        value={entry.unidad}
                        onChange={(e) => handleInputChange(index, "unidad", e.target.value)}
                        className="form-control"
                        disabled={disabled}
                    >
                        <option value="" disabled>Seleccione</option>
                        <option value="dias">Días</option>
                        <option value="porciento">Porcentaje</option>
                        <option value="pesos">Pesos</option>
                    </select>
                </div>
            ))}

            <input
                type="hidden"
                name={edit ? "edit-dynamic_data" : "dynamic_data"}
                value={JSON.stringify(items.filter(item => item.concepto || item.base || item.unidad))}
            />
        </div>
    );
};

const JornadaLaboralForm = ({register, watch, setValue, errors, initialData }) => {
  const [horarioVariableRows, setHorarioVariableRows] = useState([{ dia: '', horas: {} }]);
  const [erroresJornada, setErroresJornada] = useState({});

  const turno = watch('turno');
  const tipoHorario = watch('tipoHorario');
  const tipoJornada = watch('tipoJornada');
  const diasDescanso = watch('diasDescanso', 1);
  const tiempoDescanso = watch('tiempoDescanso', 0);
  const horarioFijo = watch('horarioFijo');
  const [hasInitialized, setHasInitialized] = useState(false);

  const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  // Límites de horas SEMANALES según el tipo de jornada
  const limitesJornadaSemanal = {
    diurno: 48,   // 48 horas semanales para diurno
    nocturno: 42, // 42 horas semanales para nocturno
    mixto: 45     // 45 horas semanales para mixto
  };

  // Límites diarios (para referencia, pero ya no se usan para validación principal)
  const limitesJornadaDiario = {
    diurno: 8,
    nocturno: 7,
    mixto: 7.5
  };

  // Horarios permitidos por tipo de turno
  const horariosPermitidos = {
    diurno: { min: 6, max: 20 },
    nocturno: { min: 20, max: 6 },
    mixto: { min: 0, max: 24 }
  };

  useEffect(() => {
    if (initialData && !hasInitialized) {
      setValue('turno', initialData.turno);
      setValue('tipoHorario', initialData.tipoHorario);
      setValue('tipoJornada', initialData.tipoJornada);
      setValue('diasDescanso', initialData.diasDescanso);
      setValue('tiempoDescanso', initialData.tiempoDescanso || 0);

      if (initialData.tipoHorario === 'fijoDiasFijos') {
        setValue('horarioFijo', initialData.horarioFijo);
      } else if (initialData.tipoHorario === 'variableDiasFijos' || initialData.tipoHorario === 'variableDescansoVariable') {
        setHorarioVariableRows(initialData.horarioVariable || [{ dia: '', horas: {} }]);
      }
      
      setHasInitialized(true);
    }
  }, [initialData, setValue, hasInitialized]);

  useEffect(() => {
    if (tipoHorario === 'variableDiasFijos' && horarioVariableRows.length < 6 && horarioVariableRows[horarioVariableRows.length - 1].dia) {
      setHorarioVariableRows([...horarioVariableRows, { dia: '', horas: {} }]);
    }
  }, [horarioVariableRows, tipoHorario]);

  // Validar que la hora esté dentro del rango permitido para el turno
  const validateTime = (time, isStart = true) => {
    if (!time || !turno) return true;
    
    const [hours, minutes] = time.split(':').map(Number);
    const totalTime = hours + (minutes / 60);
    const { min, max } = horariosPermitidos[turno];
    
    if (turno === 'diurno') {
      return totalTime >= min && totalTime < max;
    } else if (turno === 'nocturno') {
      if (isStart) {
        return totalTime >= min || totalTime < max;
      } else {
        return totalTime > min || totalTime <= max;
      }
    }
    return true;
  };

  // Calcular horas trabajadas en un día descontando el tiempo de descanso
  const calcularHorasTrabajadas = (horas, tipoJornada) => {
    if (!horas.inicio || !horas.fin) return 0;
    
    const [inicioH, inicioM] = horas.inicio.split(':').map(Number);
    const [finH, finM] = horas.fin.split(':').map(Number);
    
    let horasTrabajadas = 0;
    let inicioTotal = inicioH + (inicioM / 60);
    let finTotal = finH + (finM / 60);
    
    if (turno === 'nocturno' && finTotal < inicioTotal) {
      finTotal += 24;
    }
    
    horasTrabajadas = finTotal - inicioTotal;
    
    // Para jornada discontinua, agregar el segundo periodo
    if (tipoJornada === 'discontinua' && horas.inicio2 && horas.fin2) {
      const [inicio2H, inicio2M] = horas.inicio2.split(':').map(Number);
      const [fin2H, fin2M] = horas.fin2.split(':').map(Number);
      
      let inicio2Total = inicio2H + (inicio2M / 60);
      let fin2Total = fin2H + (fin2M / 60);
      
      if (turno === 'nocturno' && fin2Total < inicio2Total) {
        fin2Total += 24;
      }
      
      horasTrabajadas += (fin2Total - inicio2Total);
    }
    
    // Descontar tiempo de descanso (convertir minutos a horas)
    const horasDescanso = tiempoDescanso / 60;
    horasTrabajadas -= horasDescanso;
    
    return Math.max(0, horasTrabajadas);
  };

  // Calcular el total de horas semanales trabajadas
  const calcularHorasSemanales = () => {
    let totalHorasSemanales = 0;
    
    if (tipoHorario === 'fijoDiasFijos' && horarioFijo && horarioFijo.horas) {
      const horasPorDia = calcularHorasTrabajadas(horarioFijo.horas, tipoJornada);
      // Calcular días trabajados (de lunes a domingo menos días de descanso)
      const diasTrabajados = 7 - diasDescanso;
      totalHorasSemanales = horasPorDia * diasTrabajados;
    }
    
    if ((tipoHorario === 'variableDiasFijos' || tipoHorario === 'variableDescansoVariable') && horarioVariableRows.length > 0) {
      horarioVariableRows.forEach((row) => {
        if (row.horas && Object.keys(row.horas).length > 0 && row.dia) {
          const horasDia = calcularHorasTrabajadas(row.horas, tipoJornada);
          totalHorasSemanales += horasDia;
        }
      });
    }
    
    return totalHorasSemanales;
  };

  // Validar que no se exceda el límite de horas SEMANALES
  const validarLimiteHorasSemanales = () => {
    if (!turno) return true;
    
    const horasSemanales = calcularHorasSemanales();
    return horasSemanales <= limitesJornadaSemanal[turno];
  };

  // Validar todos los horarios (ahora por semana)
  const validarTodosHorarios = () => {
    const nuevosErrores = {};
    
    // Validar límite semanal
    if (!validarLimiteHorasSemanales()) {
      const horasSemanales = calcularHorasSemanales();
      nuevosErrores.horasSemanales = `La jornada semanal de ${horasSemanales.toFixed(2)}h excede el límite de ${limitesJornadaSemanal[turno]}h semanales para turno ${turno}`;
    }
    
    // También mantener validaciones diarias como advertencia (opcional)
    if (tipoHorario === 'fijoDiasFijos' && horarioFijo && horarioFijo.horas) {
      const horasTrabajadas = calcularHorasTrabajadas(horarioFijo.horas, tipoJornada);
      if (horasTrabajadas > limitesJornadaDiario[turno]) {
        nuevosErrores.horarioFijo = `Advertencia: La jornada diaria de ${horasTrabajadas.toFixed(2)}h excede el límite diario recomendado de ${limitesJornadaDiario[turno]}h`;
      }
    }
    
    if ((tipoHorario === 'variableDiasFijos' || tipoHorario === 'variableDescansoVariable') && horarioVariableRows.length > 0) {
      horarioVariableRows.forEach((row, index) => {
        if (row.horas && Object.keys(row.horas).length > 0) {
          const horasTrabajadas = calcularHorasTrabajadas(row.horas, tipoJornada);
          if (horasTrabajadas > limitesJornadaDiario[turno]) {
            nuevosErrores[`horarioVariable-${index}`] = `Advertencia: La jornada del ${row.dia || `día ${index+1}`} (${horasTrabajadas.toFixed(2)}h) excede el límite diario recomendado de ${limitesJornadaDiario[turno]}h`;
          }
        }
      });
    }
    
    setErroresJornada(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Handler para cambios en horarios variables
  const handleHorarioVariableChange = (index, field, value) => {
    const updatedRows = [...horarioVariableRows];
    updatedRows[index][field] = value;
    setHorarioVariableRows(updatedRows);
    setTimeout(validarTodosHorarios, 100);
  };

  const handleHorarioVariableTimeChange = (rowIndex, timeField, value) => {
    const updatedRows = [...horarioVariableRows];
    if (!updatedRows[rowIndex].horas) {
      updatedRows[rowIndex].horas = {};
    }
    updatedRows[rowIndex].horas[timeField] = value;
    setHorarioVariableRows(updatedRows);
    setTimeout(validarTodosHorarios, 100);
  };

  // Validación personalizada para react-hook-form
  const validateHorario = (value) => {
    if (!value || !turno) return true;
    
    if (value.inicio && !validateTime(value.inicio, true)) {
      return `Hora de inicio no válida para turno ${turno}`;
    }
    
    if (value.fin && !validateTime(value.fin, false)) {
      return `Hora de fin no válida para turno ${turno}`;
    }
    
    if (value.inicio2 && !validateTime(value.inicio2, true)) {
      return `Segunda hora de inicio no válida para turno ${turno}`;
    }
    
    if (value.fin2 && !validateTime(value.fin2, false)) {
      return `Segunda hora de fin no válida para turno ${turno}`;
    }
    
    return true;
  };

  // Obtener el texto del límite de horas para el turno actual
  const getLimiteText = () => {
    if (!turno) return "";
    return `Límite semanal: ${limitesJornadaSemanal[turno]} horas (${turno === 'diurno' ? '6:00-20:00' : turno === 'nocturno' ? '20:00-6:00' : 'flexible'})`;
  };

  // Mostrar resumen de horas semanales
  const getResumenHorasSemanales = () => {
    const horasSemanales = calcularHorasSemanales();
    const limiteSemanal = turno ? limitesJornadaSemanal[turno] : 0;
    const porcentaje = limiteSemanal > 0 ? (horasSemanales / limiteSemanal) * 100 : 0;
    
    return `Horas semanales: ${horasSemanales.toFixed(2)}h / ${limiteSemanal}h (${porcentaje.toFixed(1)}%)`;
  };

  return (
    <div className="contract-jornada">
      <div>
        <p>{getLimiteText()}</p>
        {turno && <p className="resumen-horas">{getResumenHorasSemanales()}</p>}
      </div>

      {erroresJornada.horasSemanales && (
        <div className="error">{erroresJornada.horasSemanales}</div>
      )}

      <div className="c-input">
        <label htmlFor="turno">Turno</label>
        <select
          id="turno"
          {...register("turno", { required: "Selecciona un turno" })}
        >
          <option value="">Selecciona un turno</option>
          <option value="diurno">Diurno (máx. 48 horas semanales)</option>
          <option value="nocturno">Nocturno (máx. 42 horas semanales)</option>
          <option value="mixto">Mixto (máx. 45 horas semanales)</option>
        </select>
        {errors.turno && <span className="error">{errors.turno.message}</span>}
      </div>

      <div className="c-input">
        <label htmlFor="tipoHorario">Tipo de horario</label>
        <select
          id="tipoHorario"
          {...register("tipoHorario", { required: "Selecciona un tipo de horario" })}
        >
          <option value="">Selecciona un tipo de horario</option>
          <option value="fijoDiasFijos">Horario fijo en días fijos</option>
          <option value="variableDiasFijos">Horario variable en días fijos</option>
          <option value="fijoDescansoVariable">Horario fijo y día(s) de descanso variable(s)</option>
          <option value="variableDescansoVariable">Horario variable y día(s) de descanso variable(s)</option>
        </select>
        {errors.tipoHorario && <span className="error">{errors.tipoHorario.message}</span>}
      </div>

      <div className="c-input">
        <label htmlFor="tipoJornada">Tipo de jornada</label>
        <select
          id="tipoJornada"
          {...register("tipoJornada", { required: "Selecciona un tipo de jornada" })}
        >
          <option value="">Selecciona un tipo de jornada</option>
          <option value="continua">Continua</option>
          <option value="discontinua">Discontinua</option>
        </select>
        {errors.tipoJornada && <span className="error">{errors.tipoJornada.message}</span>}
      </div>

      <div className="c-input">
        <label htmlFor="diasDescanso">Número de días de descanso</label>
        <input
          type="number"
          id="diasDescanso"
          min="1"
          max={tipoHorario === 'variableDescansoVariable' ? 6 : 7}
          {...register("diasDescanso", { 
            required: "Ingresa el número de días de descanso",
            min: { value: 1, message: "Mínimo 1 día" },
            max: { 
              value: tipoHorario === 'variableDescansoVariable' ? 6 : 7, 
              message: tipoHorario === 'variableDescansoVariable' 
                ? "Máximo 6 días para este tipo de horario" 
                : "Máximo 7 días" 
            }
          })}
        />
        {errors.diasDescanso && <span className="error">{errors.diasDescanso.message}</span>}
      </div>

      {/* HORARIO FIJO EN DÍAS FIJOS */}
      {tipoHorario === 'fijoDiasFijos' && (
        <div>
          <div>Horario Fijo</div>
          
          <div>
            <div className="c-input">
              <label htmlFor="desdeDia">Desde día</label>
              <select
                id="desdeDia"
                {...register("horarioFijo.desdeDia", { required: "Selecciona el día inicial" })}
              >
                <option value="">Selecciona un día</option>
                {diasSemana.map(dia => (
                  <option key={`desde-${dia}`} value={dia}>{dia}</option>
                ))}
              </select>
              {errors.horarioFijo?.desdeDia && <span className="error">{errors.horarioFijo.desdeDia.message}</span>}
            </div>

            <div className="c-input">
              <label htmlFor="hastaDia">Hasta día</label>
              <select
                id="hastaDia"
                {...register("horarioFijo.hastaDia", { required: "Selecciona el día final" })}
              >
                <option value="">Selecciona un día</option>
                {diasSemana.map(dia => (
                  <option key={`hasta-${dia}`} value={dia}>{dia}</option>
                ))}
              </select>
              {errors.horarioFijo?.hastaDia && <span className="error">{errors.horarioFijo.hastaDia.message}</span>}
            </div>
          </div>

          {tipoJornada === 'continua' ? (
            <div>
              <div className="c-input">
                <label htmlFor="inicio">Hora de entrada</label>
                <input
                  type="time"
                  id="inicio"
                  step="900"
                  {...register("horarioFijo.horas.inicio", { 
                    required: "Ingresa la hora de inicio",
                    validate: {
                      validTime: value => validateTime(value, true) || `Hora no válida para turno ${turno}`,
                      validSchedule: () => validateHorario(horarioFijo?.horas) === true || validateHorario(horarioFijo?.horas)
                    }
                  })}
                />
                {errors.horarioFijo?.horas?.inicio && <span className="error">{errors.horarioFijo.horas.inicio.message}</span>}
              </div>

              <div className="c-input">
                <label htmlFor="fin">Hora de salida</label>
                <input
                  type="time"
                  id="fin"
                  step="900"
                  {...register("horarioFijo.horas.fin", { 
                    required: "Ingresa la hora de fin",
                    validate: {
                      validTime: value => validateTime(value, false) || `Hora no válida para turno ${turno}`,
                      validSchedule: () => validateHorario(horarioFijo?.horas) === true || validateHorario(horarioFijo?.horas)
                    }
                  })}
                />
                {errors.horarioFijo?.horas?.fin && <span className="error">{errors.horarioFijo.horas.fin.message}</span>}
              </div>
            </div>
          ) : (
            <>
              <div>
                <div className="c-input">
                  <label htmlFor="inicio">Primera entrada</label>
                  <input
                    type="time"
                    id="inicio"
                    step="900"
                    {...register("horarioFijo.horas.inicio", { 
                      required: "Ingresa la hora de inicio",
                      validate: {
                        validTime: value => validateTime(value, true) || `Hora no válida para turno ${turno}`,
                        validSchedule: () => validateHorario(horarioFijo?.horas) === true || validateHorario(horarioFijo?.horas)
                      }
                    })}
                  />
                  {errors.horarioFijo?.horas?.inicio && <span className="error">{errors.horarioFijo.horas.inicio.message}</span>}
                </div>

                <div className="c-input">
                  <label htmlFor="fin">Primera salida</label>
                  <input
                    type="time"
                    id="fin"
                    step="900"
                    {...register("horarioFijo.horas.fin", { 
                      required: "Ingresa la hora de fin",
                      validate: {
                        validTime: value => validateTime(value, false) || `Hora no válida para turno ${turno}`,
                        validSchedule: () => validateHorario(horarioFijo?.horas) === true || validateHorario(horarioFijo?.horas)
                      }
                    })}
                  />
                  {errors.horarioFijo?.horas?.fin && <span className="error">{errors.horarioFijo.horas.fin.message}</span>}
                </div>
              </div>

              <div>
                <div className="c-input">
                  <label htmlFor="inicio2">Segunda entrada</label>
                  <input
                    type="time"
                    id="inicio2"
                    step="900"
                    {...register("horarioFijo.horas.inicio2", { 
                      required: "Ingresa la segunda hora de inicio",
                      validate: {
                        validTime: value => validateTime(value, true) || `Hora no válida para turno ${turno}`,
                        validSchedule: () => validateHorario(horarioFijo?.horas) === true || validateHorario(horarioFijo?.horas)
                      }
                    })}
                  />
                  {errors.horarioFijo?.horas?.inicio2 && <span className="error">{errors.horarioFijo.horas.inicio2.message}</span>}
                </div>

                <div className="c-input">
                  <label htmlFor="fin2">Segunda salida</label>
                  <input
                    type="time"
                    id="fin2"
                    step="900"
                    {...register("horarioFijo.horas.fin2", { 
                      required: "Ingresa la segunda hora de fin",
                      validate: {
                        validTime: value => validateTime(value, false) || `Hora no válida para turno ${turno}`,
                        validSchedule: () => validateHorario(horarioFijo?.horas) === true || validateHorario(horarioFijo?.horas)
                      }
                    })}
                  />
                  {errors.horarioFijo?.horas?.fin2 && <span className="error">{errors.horarioFijo.horas.fin2.message}</span>}
                </div>
              </div>
            </>
          )}
          
          {erroresJornada.horarioFijo && (
            <div className="error">{erroresJornada.horarioFijo}</div>
          )}
        </div>
      )}

      {/* HORARIO VARIABLE EN DÍAS FIJOS */}
      {(tipoHorario === 'variableDiasFijos' || tipoHorario === 'variableDescansoVariable') && (
        <div>
          <div>Horario Variable</div>
          {horarioVariableRows.map((row, index) => (
            <div key={index} className="horario-variable-row">
              <div className="c-input">
                <label htmlFor={`dia-${index}`}>Día</label>
                <select
                  id={`dia-${index}`}
                  value={row.dia}
                  onChange={(e) => handleHorarioVariableChange(index, 'dia', e.target.value)}
                >
                  <option value="">Selecciona un día</option>
                  {diasSemana.map(dia => (
                    <option key={`${dia}-${index}`} value={dia}>{dia}</option>
                  ))}
                </select>
              </div>

              {tipoJornada === 'continua' ? (
                <div>
                  <div className="c-input">
                    <label htmlFor={`inicio-${index}`}>Hora de entrada</label>
                    <input
                      type="time"
                      id={`inicio-${index}`}
                      step="900"
                      value={row.horas?.inicio || ''}
                      onChange={(e) => handleHorarioVariableTimeChange(index, 'inicio', e.target.value)}
                    />
                  </div>

                  <div className="c-input">
                    <label htmlFor={`fin-${index}`}>Hora de salida</label>
                    <input
                      type="time"
                      id={`fin-${index}`}
                      step="900"
                      value={row.horas?.fin || ''}
                      onChange={(e) => handleHorarioVariableTimeChange(index, 'fin', e.target.value)}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <div className="c-input">
                      <label htmlFor={`inicio-${index}`}>Primera entrada</label>
                      <input
                        type="time"
                        id={`inicio-${index}`}
                        step="900"
                        value={row.horas?.inicio || ''}
                        onChange={(e) => handleHorarioVariableTimeChange(index, 'inicio', e.target.value)}
                      />
                    </div>

                    <div className="c-input">
                      <label htmlFor={`fin-${index}`}>Primera salida</label>
                      <input
                        type="time"
                        id={`fin-${index}`}
                        step="900"
                        value={row.horas?.fin || ''}
                        onChange={(e) => handleHorarioVariableTimeChange(index, 'fin', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="c-input">
                      <label htmlFor={`inicio2-${index}`}>Segunda entrada</label>
                      <input
                        type="time"
                        id={`inicio2-${index}`}
                        step="900"
                        value={row.horas?.inicio2 || ''}
                        onChange={(e) => handleHorarioVariableTimeChange(index, 'inicio2', e.target.value)}
                      />
                    </div>

                    <div className="c-input">
                      <label htmlFor={`fin2-${index}`}>Segunda salida</label>
                      <input
                        type="time"
                        id={`fin2-${index}`}
                        step="900"
                        value={row.horas?.fin2 || ''}
                        onChange={(e) => handleHorarioVariableTimeChange(index, 'fin2', e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {erroresJornada[`horarioVariable-${index}`] && (
                <div className="error">{erroresJornada[`horarioVariable-${index}`]}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="c-input">
        <label htmlFor="tiempoDescanso">Tiempo de descanso/alimentos (minutos)</label>
        <input
          type="number"
          id="tiempoDescanso"
          min="0"
          max="120"
          {...register("tiempoDescanso", { 
            min: { value: 0, message: "Mínimo 0 minutos" },
            max: { value: 120, message: "Máximo 120 minutos" }
          })}
        />
        {errors.tiempoDescanso && <span className="error">{errors.tiempoDescanso.message}</span>}
      </div>
    </div>
  );
};

const AdditionalFields = ({contractType, register, setValue, existingData}) => {
  console.log('Existing Data in AdditionalFields:', existingData);
  const [employeeType, setEmployeeType] = useState(existingData?.tipo_empleado || '');
  const [duration, setDuration] = useState({ value: '', unit: 'dias' });

  // Efecto para cargar datos existentes
  useEffect(() => {
    console.log('Loading existing data for contract type:', contractType);
    if (existingData) {
      
      switch (contractType) {
        case "indeterminado_temporada":
          setValue('temporada', existingData.duracion_temporada || '');
          setValue('razon', existingData.razon_temporada || '');
          setValue('fin_temporada', existingData.fin_temporada || '');
          break;
        case "indeterminado_prueba":
          setValue('prueba', existingData.duracion_prueba || '');
          setEmployeeType(existingData.tipo_empleado || '');
          break;
        default:
          break;
      }
          
          
    }
  }, [existingData, setValue]);

  useEffect(() => {
    if(duration.value != ''){
      setValue('duracion', duration.value + ' ' + duration.unit)
    }
  }, [duration]);

  useEffect(() => {
    console.log('Employee type changed:', employeeType);
    if(employeeType){
      setValue('tipo_empleado', employeeType)
    }
  }, [employeeType]);

  switch (contractType) {
    case 'determinado':
      return (
        <>
          <div className="c-input">
            <label htmlFor="motivo">
              Razón o motivo del tiempo determinado
              <button type="button" class="btn btn-help" data-bs-container="body" data-bs-toggle="popover" data-bs-placement="right"  data-bs-trigger="focus"
                  data-bs-content="Por ejemplo; para cubrir la incapacidad de Juan Perez, o bien, para concluir el retraso en el proyecto contratado por el cliente constructora s.a. de c.v."
              >
                ?
              </button>
            </label>
            <textarea
              id="motivo"
              required
              {...register("motivo", { required: "Especifica la razón" })}
            />
          </div>
          <div className="c-input">
            <label>Duración del contrato</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="number"
                value={duration.value}
                onChange={(e) => setDuration({...duration, value: e.target.value})}
                required
                placeholder="Número"
              />
              <select
                value={duration.unit}
                onChange={(e) => setDuration({...duration, unit: e.target.value})}
                required
              >
                <option value="dias">Días</option>
                <option value="semanas">Semanas</option>
                <option value="meses">Meses</option>
              </select>
            </div>
          </div>
        </>
      );
    
    case 'obra':
      return (
        <>
          <div className="c-input">
            <label htmlFor="cliente">
              Nombre del cliente o proyecto
              <button type="button" class="btn btn-help" data-bs-container="body" data-bs-toggle="popover" data-bs-placement="right"  data-bs-trigger="focus"
                  data-bs-content="Aquí debe especificarse el nombre del cliente al que se le prestará la obra que da origen al contrato, o bien, el nombre del proyecto, por ejemplo: CONSTRUCTORA  S.A. DE C.V., o bien;  Casa residencial el campanario."
              >
                ?
              </button>
            </label>
            <input
              type="text"
              id="cliente"
              {...register("cliente", { required: "Especifica el cliente/proyecto" })}
            />
          </div>
          <div className="c-input">
            <label htmlFor="descripcion_obra">
              Descripción del proyecto/obra
              <button type="button" class="btn btn-help" data-bs-container="body" data-bs-toggle="popover" data-bs-placement="right"  data-bs-trigger="focus"
                  data-bs-content="Aquí debe especificarse el trabajo o trabajos a realizar motivo del contrato, proyecto u obra, por ejemplo: En la instalación de loza cerámica por 150 m2 en casa habitación, planta alta, baja, baños y terraza."
              >
                ?
              </button>
            </label>
            <textarea
              id="descripcion_obra"
              {...register("descripcion_obra", { required: "Describe el proyecto" })}
            />
          </div>
        </>
      );
    
    case 'indeterminado_prueba':
      return (
        <>
          <div className="c-input">
            <label htmlFor="employee-type">Tipo de colaborador</label>
            <select
              id="employee-type"
              value={employeeType}
              onChange={(e) => setEmployeeType(e.target.value)}
              required
            >
              <option value="">Selecciona un tipo</option>
              <option value="operativo">Operativo</option>
              <option value="direccion">Dirección/Gerencia/Profesionista/Técnico</option>
            </select>
          </div>

          <div className="c-input">
            <label htmlFor="prueba">Días de prueba</label>
            <input
              type="number"
              id="prueba"
              max={employeeType === 'operativo' ? 30 : 180}
              {...register("prueba", { 
                required: "Especifica los días de prueba",
                max: {
                  value: employeeType === 'operativo' ? 30 : 180,
                  message: employeeType === 'operativo' 
                    ? "Máximo 30 días para operativos" 
                    : "Máximo 180 días"
                }
              })}
            />
          </div>
        </>
      );
    
    case 'indeterminado_capacitacion':
      return (
        <>
          <div className="c-input">
            <label htmlFor="employee-type">Tipo de colaborador</label>
            <select
              id="employee-type"
              value={employeeType}
              onChange={(e) => setEmployeeType(e.target.value)}
              required
            >
              <option value="">Selecciona un tipo</option>
              <option value="operativo">Operativo</option>
              <option value="direccion">Dirección/Gerencia/Profesionista/Técnico</option>
            </select>
          </div>
          <div className="c-input">
            <label htmlFor="prueba">Duración de capacitación (meses)</label>
            <input
              type="number"
              id="prueba"
              max={employeeType === 'operativo' ? 3 : 6}
              {...register("prueba", { 
                required: "Especifica la duración",
                max: {
                  value: employeeType === 'operativo' ? 3 : 6,
                  message: employeeType === 'operativo' 
                    ? "Máximo 3 meses para operativos" 
                    : "Máximo 6 meses"
                }
              })}
            />
          </div>
        </>
      );
    
    case 'indeterminado':
      return null;

    case 'indeterminado_temporada':
      return(
         <>
          <div className="c-input">
            <label htmlFor="reason">
              Razón o justificación de la temporada
              <button type="button" class="btn btn-help" data-bs-container="body" data-bs-toggle="popover" data-bs-placement="right"  data-bs-trigger="hover focus"
                  data-bs-content="Aquí debes precisar el porque se considera una temporada, por ejemplo: La producción de juguete en temporada navideña, o bien; la venta de roscas de reyes en enero."
              >
                ?
              </button>
            </label>
            <textarea
              id="razon"
              {...register("razon", { required: "Especifica la razón" })}
            />
          </div>
          <div className="c-input">
            <label>
              Duración / fijación de la temporada cada año
              <button type="button" class="btn btn-help" data-bs-container="body" data-bs-toggle="popover" data-bs-placement="right"  data-bs-trigger="hover focus"
                  data-bs-content="Aquí debes especificar de que día a que día, o de que mes a que mes en el año, dura la temporada motivo del contrato."
              >
                ?
              </button>
            </label>
            <textarea
              id="temporada"
              {...register("temporada", { required: "Especifica la temporada" })}
            />
          </div>
          <div className="c-input">
            <label htmlFor="reason">
              Fecha del fin de la temporada
            </label>
            <input
              type='date'
              id="fin_temporada"
              {...register("fin_temporada", { required: "Especifica la fecha de termino de la temporada." })}
            />
          </div>
        </>
      );
    default:
      return null;
  }
};

export default Contract;